"use client";

import { useMemo, type ReactNode } from "react";
import { createTheme, ThemeProvider, StyledEngineProvider, type Theme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { enUS, jaJP } from "@mui/material/locale";
import { runtimeConfig } from "./runtime-config";
import { findTheme, MATERIAL_THEME_ID } from "./themes";
import { useTranslation } from "./i18n/context";

/**
 * The shared MUI theme for all six apps.
 *
 * # WHY THE THEME LIVES HERE AND NOT IN EACH APP
 *
 * Same reason every other shared thing does: `apps/*` are mostly re-export
 * shims, and the components being themed are in this package. An app supplies
 * only what genuinely differs — its surface.
 *
 * # COLOUR COMES FROM THE VERTICAL'S THEME, NOT THE APP
 *
 * Each vertical is fixed to a theme by `THEME` in its `.env` (it used to be an
 * admin choice); `themes.ts` has the palettes and where each was taken from. The
 * server resolves the id per request and templates it into `runtimeConfig.theme`,
 * so the first frame is already the right colour — nothing here waits on a
 * fetch. Not per app on purpose: each app serves more than one vertical (the
 * clinic app is both clinics), and they are different colours.
 *
 * With no id — Vite dev, every e2e stack — the palette is exactly stock
 * Material, which is what the screenshot baselines were taken with.
 *
 * Two colours matter to the chrome: `palette.primary` for actions, and
 * `palette.frame` for the bars that frame the page — every AppBar and the liff
 * hero band — so a theme recolours them without the components knowing themes
 * exist. Under stock Material the frame IS the primary, as it always was.
 */
export interface BrandPalette {
  /** A theme's primary colour. Becomes `palette.primary.main`. */
  main: string;
  /** A theme's secondary colour, when it has one. */
  accent?: string;
  /** A theme's frame colour, when it differs from the primary. */
  frame?: string;
  /** A theme's page colour behind the cards, when it has its own. */
  ground?: string;
}

/*
  `palette.frame`: the colour of the bars that frame a page (AppBar, the liff
  hero band). Declared here because the palette is MUI's type, and this file is
  the one place the theme is built.
*/
declare module "@mui/material/styles" {
  interface Palette {
    frame: string;
  }
  interface PaletteOptions {
    frame?: string;
  }
}

export interface AppThemeOptions {
  /**
   * Which surface this is.
   *
   * Not cosmetic: admin is a dense desktop tool driven with a mouse, liff is a
   * phone screen inside the LINE webview where a mis-tap is the top complaint.
   * They want different default sizes, and encoding that here keeps it out of
   * every component.
   */
  surface: "admin" | "liff";
  /** Absent means stock Material. `AppTheme` sets it from the vertical's theme. */
  brand?: BrandPalette;
  /** `"ja"` or `"en"`; selects MUI's own strings for aria labels and pagination. */
  lang?: string;
}

export function createAppTheme({ surface, brand, lang }: AppThemeOptions): Theme {
  // Stock Material's blue when there is no brand, so an unthemed bar looks
  // exactly as it did before `frame` existed.
  const frame = brand?.frame ?? brand?.main ?? "#1976d2";
  return createTheme(
    {
      palette: {
        frame,
        ...(brand
          ? {
              primary: { main: brand.main },
              ...(brand.accent ? { secondary: { main: brand.accent } } : {}),
            }
          : {}),
        /*
          A tinted page behind white cards, rather than MUI's default #fff.

          Both surfaces are a stack of Paper — liff's service cards, step panels
          and receipt; admin's tiles, panels and tables — and on a pure-white
          page those lose their edges and the screen reads as one undivided
          sheet. This is not a new choice: `--bg` in the deleted tokens.css was
          `--neutral-50`, i.e. this exact value, on both surfaces.

          Worth stating because it was briefly wrong. When CssBaseline went in it
          was set for liff only, on the reasoning that "admin's own stylesheet
          still sets the page" — which stopped being true in the same commit that
          deleted that stylesheet, and admin went white for a day.
        */
        background: { default: brand?.ground ?? "#f8fafc" },
      },
      typography: {
        /*
          htmlFontSize stays at MUI's default 16 and the root <html> font-size is
          being dropped from tokens.css to match.

          The old tokens.css set `html { font-size: 112.5% }` (an 18px root) and
          each surface undid it to 100% below 768px. Leaving that in place would
          have meant a real root of 18px while MUI's rem arithmetic assumes 16,
          so every component would render 12.5% larger than designed. Scale is
          set here instead, where it is visible, and the root is pinned to 100%
          in the CssBaseline override below.

          16 for admin reproduces roughly what the 18px root delivered on
          desktop; liff keeps MUI's default 14, which is what the old mobile
          100% override already amounted to.
        */
        fontSize: surface === "admin" ? 16 : 14,
        // The Japanese faces the old stack named, kept ahead of MUI's Roboto
        // default: most content in every vertical is Japanese, and Roboto has no
        // CJK coverage, so without this the browser silently falls back per glyph
        // and the type mixes two faces mid-sentence.
        fontFamily: [
          "Roboto",
          "-apple-system",
          "BlinkMacSystemFont",
          '"Segoe UI"',
          '"Hiragino Sans"',
          '"Noto Sans JP"',
          "sans-serif",
        ].join(","),
      },
      components: {
        /*
          THE ROOT FONT SIZE, and this is load-bearing.

          tokens.css set `html { font-size: 112.5% }` — an 18px root — and MUI's
          rem arithmetic assumes 16 (`htmlFontSize`), so on an 18px root every
          component renders 12.5% larger than designed. Both stylesheets used to
          undo it in a `@media (max-width: 768px)` block; they are gone, and the
          correction is unconditional because neither surface wants the 18px.

          Percentage rather than px, so the reader's own browser font-size
          preference is still honoured.
        */
        MuiCssBaseline: {
          styleOverrides: {
            html: { fontSize: "100%" },
            // No page should ever scroll the document sideways on a phone.
            body: { overflowX: "hidden" },
            /*
              Admin headings start flush. Pages open with a bare <h2>, whose
              browser top margin (~20px) stacked on the main area's own padding
              and left a band of empty space under the topbar on every screen;
              card <h3>s did the same inside their padding. liff spaces its
              headings through components and is left alone.
            */
            ...(surface === "admin" ? { "h1, h2, h3": { marginTop: 0 } } : {}),
          },
        },
        /*
          Sentence case, not MUI's ALL CAPS. Japanese has no case, so nobody saw
          it — but in English every button read "RESERVE THIS PLAN", "LOGOUT",
          "DELETE". MuiButton gets the same override in each surface's branch
          below, beside its size.
        */
        MuiTab: { styleOverrides: { root: { textTransform: "none" } } },
        /*
          Every AppBar -- the liff header, the admin topbar -- is painted in the
          frame colour, so a booking site's bar matches its business's website
          header. `color="primary"` bars only: an AppBar given another colour
          on purpose keeps it.
        */
        MuiAppBar: {
          styleOverrides: {
            colorPrimary: { backgroundColor: frame, color: "#fff" },
          },
        },
        MuiToggleButton: { styleOverrides: { root: { textTransform: "none" } } },
        ...(surface === "liff"
          ? {
              // 48px is the touch target this surface needs; MUI's default is 36.
              MuiButton: {
                defaultProps: { size: "large" },
                styleOverrides: { root: { textTransform: "none" } },
              },
              /*
                Every liff page's outermost box. This reproduces the `.container`
                rule that used to wrap them all — 480px, centred, 16px in — as a
                theme default, so the pages say `<Container>` and nothing else.

                `maxWidth={false}` turns OFF MUI's breakpoint widths, which is
                what lets `root.maxWidth` below take effect at every size. The
                paddings are spelled out longhand on purpose: the `padding`
                shorthand would be overridden by the gutter rules MUI puts in
                this same slot, which set paddingLeft/Right specifically.

                480px matters beyond taste — the Hero (liff/components/PageChrome)
                bleeds out of it with negative margins of exactly this padding,
                so the 16px has to stay 16px.

                The 480 itself is the PHONE column only. A phone-sized strip
                centred in a desktop viewport reads as a bug, so from sm up the
                column widens (560 tablet, 720 desktop, capped): the same pages,
                using the screen a web visitor actually has. ≤480 is untouched —
                every liff page below sm renders exactly as before, which is
                what the Pixel-5 visual baselines pin.
              */
              MuiContainer: {
                defaultProps: { maxWidth: false },
                styleOverrides: {
                  root: ({ theme }) => ({
                    maxWidth: 480,
                    paddingTop: 16,
                    paddingBottom: 16,
                    paddingLeft: 16,
                    paddingRight: 16,
                    [theme.breakpoints.up("sm")]: { maxWidth: 560 },
                    [theme.breakpoints.up("md")]: { maxWidth: 720 },
                  }),
                },
              },
            }
          : {
              MuiButton: {
                defaultProps: { size: "small" },
                styleOverrides: { root: { textTransform: "none" } },
              },
              MuiTextField: { defaultProps: { size: "small" } },
              /*
                Dense rows. The admin's lists are read, not tapped: a day's
                bookings should fit the screen without scrolling past
                half-empty rows. ResponsiveTable turns them into cards on a
                phone, where the size no longer applies.
              */
              MuiTable: { defaultProps: { size: "small" } },
            }),
      },
    },
    lang === "en" ? enUS : jaJP,
  );
}

/**
 * Mounts the theme. Goes inside each app's `I18nProvider` so `lang` is readable,
 * and wraps everything below it.
 *
 * # CSSBASELINE, ON BOTH SURFACES
 *
 * It went in per surface, in the phase that deleted that surface's stylesheet,
 * because its `body` and typography resets would otherwise have fought whatever
 * was left. Both stylesheets are gone now, so it is unconditional.
 *
 * # `StyledEngineProvider injectFirst`
 *
 * It made Emotion inject MUI's `<style>` elements ahead of the Vite-emitted
 * stylesheet `<link>`, so the legacy CSS won ties while the two coexisted. No
 * stylesheet ships any more, so it now decides nothing; it stays because taking
 * it out is a cascade change with nothing to gain.
 *
 * The CSP needs nothing for this: `style-src` is `'self' 'unsafe-inline'` with
 * no nonce or hash in that directive (`go/internal/core/spa/spa.go`), so
 * Emotion's runtime injection is already permitted, on the enforced admin policy
 * as well as the report-only liff one.
 */
export function AppTheme({
  children,
  surface,
  lang,
}: Omit<AppThemeOptions, "brand"> & { children: ReactNode }) {
  // Both surfaces mount this inside their I18nProvider, and liff's re-exports
  // this same hook, so the active language is always readable here. Without it
  // MUI's own strings (pagination, aria labels) stayed Japanese in English mode.
  const { lang: activeLang } = useTranslation();
  const effectiveLang = lang ?? activeLang;
  const themeId = findTheme(runtimeConfig.theme).id;
  const theme = useMemo(() => {
    const def = findTheme(themeId);
    return createAppTheme({
      surface,
      lang: effectiveLang,
      // Material passes no palette at all, so it is stock to the last shade
      // rather than a re-derivation of MUI's blue.
      brand:
        def.id === MATERIAL_THEME_ID
          ? undefined
          : { main: def.primary, accent: def.secondary, frame: def.frame, ground: def.ground },
    });
  }, [surface, effectiveLang, themeId]);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
